import {
  Activity, Bell, BookOpen, Braces, ChevronDown, Crosshair, FileClock, Filter,
  BrickWallShield, Gauge, ListRestart, LogOut, Menu, Network, RefreshCw, Server, Settings,
  ShieldCheck, SlidersHorizontal, UserCog, Users, X,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { PageErrorBoundary, Toast, Toggle } from './UI';
import { TelegramSettings } from './TelegramSettings';

const sections = [
  {
    label: 'Monitoring',
    items: [
      ['Dashboard', '/app', Gauge], ['Subnets', '/app/subnets', Network], ['Attack history', '/app/attacks', Crosshair],
      ['Scrubbers', '/app/scrubbers', Server, true], ['Filter traffic', '/app/filter-traffic', Filter, true],
      ['Profiles', '/app/profiles', SlidersHorizontal],
    ],
  },
  {
    label: 'Reference',
    items: [['Scrubber guide', '/app/guide', BookOpen], ['API reference', '/app/api-reference', Braces]],
  },
  {
    label: 'Administration', admin: true,
    items: [
      ['Cloud firewall', '/app/firewall', BrickWallShield], ['IP whitelist', '/app/whitelist', ShieldCheck],
      ['Service probes', '/app/service-probes', Activity],
      ['Users', '/app/users', Users], ['Audit log', '/app/audit', FileClock], ['Global config', '/app/global-config', Settings],
    ],
  },
];

export function Layout() {
  const { user, isAdmin, logout } = useAuth();
  const { autoRefresh, setAutoRefresh, windowsNT, setWindowsNT } = useUI();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [telegramOpen, setTelegramOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    await queryClient.refetchQueries({ type: 'active' });
    setRefreshing(false);
  };

  const signOut = () => {
    logout();
    navigate('/app/login', { replace: true });
  };

  return (
    <div className={`app-shell ${windowsNT ? 'windows-nt' : ''}`}>
      <aside className={`sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="sidebar-brand"><span className="product-mark"><ShieldCheck size={21} /></span><strong>DDoS Cluster</strong><button className="icon-button mobile-only" onClick={() => setSidebarOpen(false)}><X size={18} /></button></div>
        <nav className="sidebar-nav">
          {sections.filter((section) => !section.admin || isAdmin).map((section) => (
            <section key={section.label}>
              <h2>{section.label}</h2>
              {section.items.filter((item) => !item[3] || isAdmin).map(([label, to, Icon]) => (
                <NavLink key={to} to={to} end={to === '/app'} onClick={() => setSidebarOpen(false)}>
                  <Icon size={17} /><span>{label}</span>
                </NavLink>
              ))}
            </section>
          ))}
        </nav>
        <footer><span className="connection-dot" />Connected</footer>
      </aside>
      {sidebarOpen && <button className="sidebar-scrim" onClick={() => setSidebarOpen(false)} aria-label="Close navigation" />}

      <header className="topbar">
        <div className="topbar-left">
          <button className="icon-button mobile-only" onClick={() => setSidebarOpen(true)} title="Open navigation"><Menu size={19} /></button>
          <span className="route-label">{sections.flatMap((section) => section.items).find((item) => item[1] === location.pathname)?.[0] || 'Control panel'}</span>
        </div>
        <div className="topbar-actions">
          <Toggle checked={autoRefresh} onChange={setAutoRefresh} label="Live" />
          <button className="icon-button" onClick={refresh} title="Refresh current data" disabled={refreshing}><RefreshCw className={refreshing ? 'spin' : ''} size={18} /></button>
          <div className="account-menu">
            <button className="account-button" onClick={() => setAccountOpen((value) => !value)}><span>{user?.username}</span><small>{user?.role}</small><ChevronDown size={15} /></button>
            {accountOpen && (
              <div className="account-dropdown">
                <button onClick={() => { setTelegramOpen(true); setAccountOpen(false); }}><Bell size={16} />Telegram alerts</button>
                <label><Toggle checked={windowsNT} onChange={setWindowsNT} /><span>Windows NT mode</span></label>
                <button className="danger-text" onClick={signOut}><LogOut size={16} />Sign out</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="content"><PageErrorBoundary resetKey={location.pathname}><Outlet /></PageErrorBoundary></main>
      {telegramOpen && <TelegramSettings onClose={() => setTelegramOpen(false)} />}
      <Toast />
    </div>
  );
}
